import {
  DescriptionOutlined,
  VisibilityOutlined,
  FileDownloadOutlined,
  TrendingUp,
  OpenInNewOutlined,
  ScheduleOutlined,
  SendOutlined,
  EditOutlined,
  DeleteOutlined,
  EmailOutlined,
} from '@mui/icons-material';
import { CardContainer, DataTable, Tag, Button, Dropdown } from 'finara-components';
import type { DataTableColumn } from 'finara-components';

import { CsvExportButton } from '@/components/common';
import {
  generatedReports,
  reportTemplates,
  employeeMovements,
  receipts,
  scheduledReports,
  monthlyMetrics,
  departmentActivity,
} from '@/data/admin/reports/reportsData';
import type { EmployeeMovement } from '@/data/admin/reports/reportsData';
import { formatNumber } from '@/utils/formatters';

export const GeneralReportsTab: React.FC = () => {
  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      nomina: 'nomina',
      adelanto: 'adelanto',
      reembolso: 'reembolso',
      bonus: 'bonus',
    };
    return labels[type] || type;
  };

  const getTypeVariant = (type: string) => {
    const variants: Record<string, 'blue' | 'red' | 'yellow' | 'green'> = {
      nomina: 'blue',
      adelanto: 'red',
      reembolso: 'yellow',
      bonus: 'yellow',
    };
    return variants[type] || 'blue';
  };

  const movementsColumns: DataTableColumn<EmployeeMovement>[] = [
    {
      key: 'date',
      label: 'Fecha',
      render: (_, movement) => (
        <div>
          <p className="text-sm text-gray-900">{movement.date}</p>
          <p className="text-xs text-gray-500">{movement.time}</p>
        </div>
      ),
    },
    {
      key: 'employee',
      label: 'Empleado',
      render: (_, movement) => (
        <div>
          <p className="text-sm text-gray-900">{movement.employee}</p>
          <p className="text-xs text-gray-500">{movement.employeeId}</p>
        </div>
      ),
    },
    {
      key: 'department',
      label: 'Departamento',
      render: (_, movement) => <span className="text-sm text-gray-900">{movement.department}</span>,
    },
    {
      key: 'type',
      label: 'Tipo',
      render: (_, movement) => (
        <Tag variant={getTypeVariant(movement.type)}>{getTypeLabel(movement.type)}</Tag>
      ),
    },
    {
      key: 'amount',
      label: 'Monto',
      render: (_, movement) => (
        <span className="text-sm font-semibold text-gray-900">
          ${formatNumber(movement.amount)}
        </span>
      ),
    },
    {
      key: 'status',
      label: 'Estado',
      render: (_, movement) => <Tag variant="green">{movement.status}</Tag>,
    },
    {
      key: 'reference',
      label: 'Referencia',
      render: (_, movement) => <span className="text-sm text-gray-900">{movement.reference}</span>,
    },
    {
      key: 'actions',
      label: 'Acciones',
      render: _ => (
        <div className="flex items-center gap-2">
          <button
            className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
            title="Ver detalles"
          >
            <VisibilityOutlined fontSize="small" />
          </button>
          <button
            className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
            title="Descargar"
          >
            <FileDownloadOutlined fontSize="small" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Generation Section */}
      <CardContainer>
        <div>
          <div className="flex items-center gap-2 mb-6">
            <DescriptionOutlined className="text-gray-700" />
            <h3 className="text-lg font-semibold text-gray-900">Generación de Reportes</h3>
          </div>

          <div className="grid grid-cols-1 lg:!grid-cols-2 gap-6">
            {/* Plantillas Empresariales */}
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-4">Plantillas Empresariales</h4>
              <div className="space-y-3">
                {reportTemplates.map(template => (
                  <div
                    key={template.id}
                    className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
                  >
                    <div className="flex items-start gap-3">
                      <DescriptionOutlined className="text-gray-400 mt-1" fontSize="small" />
                      <div>
                        <p className="font-medium text-gray-900">{template.name}</p>
                        <p className="text-sm text-gray-500">{template.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Reportes Generados */}
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-4">Reportes Generados</h4>
              <div className="space-y-3">
                {generatedReports.map(report => (
                  <div
                    key={report.id}
                    className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <p className="font-medium text-gray-900">{report.title}</p>
                        <p className="text-sm text-gray-500">{report.employees}</p>
                        <p className="text-xs text-gray-500">{report.period}</p>
                      </div>
                      <Tag variant={report.status === 'Listo' ? 'green' : 'blue'}>
                        {report.status}
                      </Tag>
                    </div>
                    {report.status === 'Listo' && (
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-200">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-500">{report.format}</span>
                          <span className="text-xs text-gray-500">{report.size}</span>
                        </div>
                        <Button variant="white" size="small" startIcon={<FileDownloadOutlined />}>
                          Descargar
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </CardContainer>

      {/* Employee Movements Table */}
      <CardContainer>
        <div>
          <div className="flex flex-col lg:!flex-row lg:!items-center lg:!justify-between mb-6">
            <div className="flex items-center gap-2 mb-4 lg:!mb-0">
              <TrendingUp className="text-gray-700" />
              <h3 className="text-lg font-semibold text-gray-900">Movimientos de Empleados</h3>
            </div>
            <div className="flex items-center gap-3">
              <CsvExportButton
                data={employeeMovements}
                filename="movimientos_empleados_excel"
                columns={movement => ({
                  Fecha: movement.date,
                  Hora: movement.time,
                  Empleado: movement.employee,
                  'ID Empleado': movement.employeeId,
                  Departamento: movement.department,
                  Tipo: getTypeLabel(movement.type),
                  Monto: formatNumber(movement.amount),
                  Estado: movement.status,
                  Referencia: movement.reference,
                })}
                variant="white"
                size="small"
                buttonText="Exportar Excel"
              />
              <CsvExportButton
                data={employeeMovements}
                filename="movimientos_empleados"
                columns={movement => ({
                  Fecha: movement.date,
                  Hora: movement.time,
                  Empleado: movement.employee,
                  'ID Empleado': movement.employeeId,
                  Departamento: movement.department,
                  Tipo: getTypeLabel(movement.type),
                  Monto: formatNumber(movement.amount),
                  Estado: movement.status,
                  Referencia: movement.reference,
                })}
                variant="white"
                size="small"
                buttonText="Exportar CSV"
              />
            </div>
          </div>
          <div className="overflow-x-auto">
            <DataTable columns={movementsColumns} data={employeeMovements} />
          </div>
        </div>
      </CardContainer>

      {/* Receipts Section */}
      <CardContainer>
        <div>
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <DescriptionOutlined className="text-gray-700" />
              <h3 className="text-lg font-semibold text-gray-900">Comprobantes Empresariales</h3>
            </div>
            <Button variant="white" size="small" startIcon={<FileDownloadOutlined />}>
              Descargar Lote Completo
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:!grid-cols-2 gap-6">
            {/* Comprobantes Disponibles */}
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-4">Comprobantes Disponibles</h4>
              <div className="space-y-3">
                {receipts.map(receipt => (
                  <CardContainer
                    key={receipt.id}
                    className="flex flex-col lg:!flex-row lg:!items-start lg:!justify-between"
                  >
                    <div>
                      <div>
                        <p className="font-medium text-gray-900">{receipt.reference}</p>
                        <p className="text-sm text-gray-500">{receipt.employee}</p>
                        <div className="flex items-center gap-2 mt-2">
                          {receipt.tags.map((tag, index) => (
                            <Tag key={index} variant="white">
                              {tag}
                            </Tag>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-3">
                      <Button variant="white" size="small" startIcon={<VisibilityOutlined />}>
                        Ver
                      </Button>
                      <Button variant="white" size="small" startIcon={<FileDownloadOutlined />}>
                        PDF
                      </Button>
                    </div>
                  </CardContainer>
                ))}
              </div>
            </div>

            {/* Vista Previa PDF */}
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-4">Vista Previa PDF</h4>
              <CardContainer>
                <div className="p-12 text-center">
                  <DescriptionOutlined
                    className="text-gray-400 mx-auto mb-4"
                    style={{ fontSize: '4rem' }}
                  />
                  <p className="text-gray-600">Selecciona un comprobante para visualizar</p>
                </div>
              </CardContainer>
            </div>
          </div>
        </div>
      </CardContainer>

      {/* Monthly Analysis */}
      <CardContainer>
        <div>
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <TrendingUp className="text-gray-700" />
              <h3 className="text-lg font-semibold text-gray-900">Análisis Empresarial</h3>
            </div>
            <button className="flex items-center gap-2 px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-md">
              <OpenInNewOutlined fontSize="small" />
              Ver Análisis Detallado
            </button>
          </div>

          <div className="grid grid-cols-1 lg:!grid-cols-2 gap-6">
            {/* Monthly Metrics */}
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-4">Métricas del Mes</h4>
              <div className="space-y-4">
                {monthlyMetrics.map((metric, index) => (
                  <CardContainer key={index} className="space-y-4">
                    <span className="text-sm text-gray-700">{metric.label}</span>
                    <div className="text-xl font-semibold text-gray-900">{metric.value}</div>
                    {metric.trend && <div className="text-xs">{metric.trend}</div>}
                  </CardContainer>
                ))}
              </div>
            </div>

            {/* Department Activity */}
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-4">
                Departamentos con Mayor Actividad
              </h4>
              <div className="space-y-4">
                {departmentActivity.map((dept, index) => (
                  <CardContainer key={index}>
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{dept.name}</p>
                        <p className="text-sm text-gray-500">{dept.operations} operaciones</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-gray-900">${formatNumber(dept.volume)}</p>
                        <p className="text-xs text-gray-500">Volumen</p>
                      </div>
                    </div>
                  </CardContainer>
                ))}
              </div>
            </div>
          </div>
        </div>
      </CardContainer>

      {/* Scheduled Reports Section */}
      <CardContainer>
        <div>
          <div className="flex items-center gap-2 mb-6">
            <ScheduleOutlined className="text-gray-700" />
            <h3 className="text-lg font-semibold text-gray-900">Reportes Programados</h3>
          </div>

          <div className="grid grid-cols-1 lg:!grid-cols-2 gap-6">
            {/* Left Side - Nueva Programación */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-sm font-semibold text-gray-900">Nueva Programación</h4>
                <Button variant="white" size="small" startIcon={<SendOutlined />}>
                  Crear Programación
                </Button>
              </div>

              <div className="space-y-4">
                <div>
                  <label
                    htmlFor="report-type"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Tipo de Reporte
                  </label>
                  <Dropdown
                    id="report-type"
                    className="w-full"
                    value=""
                    onChange={() => {}}
                    options={[
                      { value: 'nomina', label: 'Resumen de Nómina' },
                      { value: 'adelantos', label: 'Reporte de Adelantos' },
                      { value: 'departamento', label: 'Operaciones por Departamento' },
                    ]}
                    placeholder="Seleccionar tipo de reporte"
                  />
                </div>

                <div>
                  <label
                    htmlFor="frequency"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Frecuencia
                  </label>
                  <Dropdown
                    id="frequency"
                    className="w-full"
                    value=""
                    onChange={() => {}}
                    options={[
                      { value: 'semanal', label: 'Semanal' },
                      { value: 'mensual', label: 'Mensual' },
                    ]}
                    placeholder="Seleccionar frecuencia"
                  />
                </div>

                <div>
                  <label htmlFor="format" className="block text-sm font-medium text-gray-700 mb-2">
                    Formato
                  </label>
                  <Dropdown
                    id="format"
                    className="w-full"
                    value=""
                    onChange={() => {}}
                    options={[
                      { value: 'pdf', label: 'PDF' },
                      { value: 'excel', label: 'Excel' },
                    ]}
                    placeholder="Seleccionar formato"
                  />
                </div>

                <div>
                  <label htmlFor="day" className="block text-sm font-medium text-gray-700 mb-2">
                    Día de Envío
                  </label>
                  <Dropdown
                    id="day"
                    className="w-full"
                    value=""
                    onChange={() => {}}
                    options={[
                      { value: 'lunes', label: 'Lunes' },
                      { value: 'martes', label: 'Martes' },
                      { value: 'miercoles', label: 'Miércoles' },
                      { value: 'jueves', label: 'Jueves' },
                      { value: 'viernes', label: 'Viernes' },
                    ]}
                    placeholder="Seleccionar día"
                  />
                </div>

                <div>
                  <label
                    htmlFor="recipients"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Destinatarios
                  </label>
                  <input
                    id="recipients"
                    type="text"
                    placeholder="admin@techcorp.com, finanzas@techcorp.com"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
            </div>

            {/* Right Side - Programaciones Activas */}
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-4">Programaciones Activas</h4>
              <div className="space-y-3">
                {scheduledReports.map(report => (
                  <div
                    key={report.id}
                    className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{report.name}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Tag variant="blue">{report.frequency}</Tag>
                          <Tag variant="white">{report.format}</Tag>
                          <Tag variant="green">{report.status}</Tag>
                        </div>
                      </div>
                    </div>
                    <div className="text-sm text-gray-500 mt-2">
                      <p>Próximo envío: {report.nextSend}</p>
                      <p className="flex items-center gap-1">
                        <EmailOutlined fontSize="small" />
                        {report.recipients} destinatarios
                      </p>
                    </div>
                    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-gray-200">
                      <Button variant="white" size="small" startIcon={<EditOutlined />}>
                        Editar
                      </Button>
                      <Button variant="white" size="small" startIcon={<SendOutlined />}>
                        Enviar Ahora
                      </Button>
                      <button className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded ml-auto">
                        <DeleteOutlined fontSize="small" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </CardContainer>
    </div>
  );
};
